/**
 * Which scheduled work block "start" means right now.
 *
 * Twin of `work_blocks.pick_startable_block` / Mac `WorkBlockChoice` so Daily
 * and the menu bar cannot disagree with the server about what a start click
 * will open.
 */

export const STARTABLE_GRACE_MS = 30 * 60_000;

export interface StartableBlockLike {
  id: number;
  status: string;
  starts_at: string;
  ends_at: string;
}

export function pickStartableBlock<T extends StartableBlockLike>(
  blocks: T[],
  now = Date.now(),
): T | null {
  const candidates = blocks.filter(
    (b) =>
      b.status === 'scheduled' &&
      new Date(b.ends_at).getTime() + STARTABLE_GRACE_MS > now,
  );
  if (candidates.length === 0) return null;

  const current = candidates
    .filter((b) => {
      const start = new Date(b.starts_at).getTime();
      const end = new Date(b.ends_at).getTime();
      return start <= now && now < end;
    })
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  if (current[0]) return current[0];

  const upcoming = candidates
    .filter((b) => new Date(b.starts_at).getTime() > now)
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  if (upcoming[0]) return upcoming[0];

  return candidates.reduce((best, b) =>
    new Date(b.ends_at).getTime() > new Date(best.ends_at).getTime() ? b : best,
  );
}

/** All scheduled blocks still within the startable grace (for listing). */
export function listStartableBlocks<T extends StartableBlockLike>(
  blocks: T[],
  now = Date.now(),
): T[] {
  return blocks
    .filter(
      (b) =>
        b.status === 'scheduled' &&
        new Date(b.ends_at).getTime() + STARTABLE_GRACE_MS > now,
    )
    .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
}
